import { Subproject } from '@/stores/SubprojectStore';
import {
  Box,
  Button,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { parseAmount, SubprojectBreakdownState } from './types';

type Mode = '$' | '%';

/** The four money fields on a breakdown row — everything but the row's id. */
type AmountField = Exclude<keyof SubprojectBreakdownState, 'subprojectId'>;

/**
 * Percentages the user is part-way through typing, keyed by row + amount field.
 *
 * Dollars are the stored representation, but deriving the input's value back
 * from them snaps every keystroke to one decimal place, which makes multi-digit
 * entry and deletion impossible (see
 * .csi/fix-percent-cost-estimate/_constraints.md). While a field is being edited
 * in % mode its raw text lives here instead.
 *
 * The key carries the field name, which encodes the FY/Total scope, so a draft
 * left behind on the other tab can never be read back against this tab's totals.
 */
type PctDrafts = Record<string, string>;

function draftKey(subprojectId: string, field: AmountField): string {
  return `${subprojectId}:${field}`;
}

/**
 * Reduces input to what a positive decimal is made of: digits and at most one
 * decimal point. Typing is unaffected (a rejected character simply leaves the
 * value as it was), and a paste of '12.5%' or '1,000' lands as a number instead
 * of being dropped whole.
 */
function sanitizePct(raw: string): string {
  const digitsAndDots = raw.replace(/[^\d.]/g, '');
  const firstDot = digitsAndDots.indexOf('.');
  if (firstDot === -1) return digitsAndDots;
  return (
    digitsAndDots.slice(0, firstDot + 1) +
    digitsAndDots.slice(firstDot + 1).replace(/\./g, '')
  );
}

/**
 * Dollars for a typed percentage. Returns '' for input that isn't a number yet
 * ('', '.') and for a line item whose total hasn't been entered — the draft is
 * still held, so the dollars fill in once a total arrives.
 */
function pctToAmount(raw: string, total: number | null): string {
  const pct = parseAmount(raw);
  if (pct === null || total === null) return '';
  return ((pct / 100) * total).toFixed(2);
}

const COL_LABEL = '1fr';
const COL_AMOUNT = '160px';

const amountFieldSx = {
  '& input': { textAlign: 'right', color: 'primary.main' },
  '& .MuiInputAdornment-root': { color: 'primary.main' },
  '& .MuiOutlinedInput-root': {
    '& fieldset': { borderColor: 'rgba(14, 70, 63, 0.3)' },
    '&:hover fieldset': { borderColor: 'rgba(14, 70, 63, 0.5)' },
    '&.Mui-focused fieldset': { borderColor: '#0E463F', borderWidth: 2 },
  },
  '& legend': { display: 'none' },
  '& .MuiOutlinedInput-notchedOutline': { top: 0 },
} as const;

function formatDollar(n: number): string {
  return Math.abs(n).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatRemaining(n: number): string {
  return n < 0 ? `-$${formatDollar(n)}` : `$${formatDollar(n)}`;
}

interface SubprojectBreakdownProps {
  ownerTotal: string;
  contractorTotal: string;
  scope?: 'fy' | 'total';
  subprojects: Subproject[];
  breakdowns: SubprojectBreakdownState[];
  onChange: (breakdowns: SubprojectBreakdownState[]) => void;
}

export function SubprojectBreakdown({
  ownerTotal,
  contractorTotal,
  scope = 'fy',
  subprojects,
  breakdowns,
  onChange,
}: SubprojectBreakdownProps) {
  const ownerField: AmountField =
    scope === 'fy' ? 'ownerAmountFiscalyear' : 'ownerAmountTotal';
  const contractorField: AmountField =
    scope === 'fy' ? 'contractorAmountFiscalyear' : 'contractorAmountTotal';

  const ownerTotalNum = parseAmount(ownerTotal);
  const contractorTotalNum = parseAmount(contractorTotal);

  // Each breakdown instance owns its own toggle: Capital's Construction and the
  // two Operation utilities are independent tables that happen to share a component.
  const [mode, setMode] = useState<Mode>('$');
  const [pctDrafts, setPctDrafts] = useState<PctDrafts>({});

  function changeMode(next: Mode) {
    // Both buttons stay clickable in both modes, so re-clicking the active one
    // must not discard in-progress work.
    if (next === mode) return;
    // A draft only makes sense against the mode it was typed in; after a switch
    // the input goes back to being derived from the stored dollars.
    setPctDrafts({});
    setMode(next);
  }

  // Held percentages are relative to the line item's total, so a total that
  // changes underneath them (or arrives late) has to be re-applied — otherwise
  // the stored dollars keep describing the old total. Only this scope's fields
  // are read, so a draft left on the other FY/Total tab is ignored rather than
  // applied against the wrong total.
  useEffect(() => {
    // `onChange` is an inline arrow in both parents, so this re-runs on every
    // keystroke anywhere on the screen. With no drafts held there is nothing to
    // re-apply, which is the overwhelmingly common case — leave before the map.
    if (Object.keys(pctDrafts).length === 0) return;

    let changed = false;
    const next = breakdowns.map((b) => {
      const patch: Partial<SubprojectBreakdownState> = {};
      const cells = [
        [ownerField, ownerTotalNum],
        [contractorField, contractorTotalNum],
      ] as const;
      for (const [field, total] of cells) {
        const raw = pctDrafts[draftKey(b.subprojectId, field)];
        if (raw === undefined) continue;
        const amount = pctToAmount(raw, total);
        if (b[field] !== amount) patch[field] = amount;
      }
      if (Object.keys(patch).length === 0) return b;
      changed = true;
      return { ...b, ...patch };
    });
    // The equality guard makes a re-run a no-op, so a full dependency list
    // settles after one extra pass instead of looping.
    if (changed) onChange(next);
  }, [
    breakdowns,
    onChange,
    pctDrafts,
    ownerField,
    contractorField,
    ownerTotalNum,
    contractorTotalNum,
  ]);

  function getBreakdown(subprojectId: string): SubprojectBreakdownState {
    return (
      breakdowns.find((b) => b.subprojectId === subprojectId) ?? {
        subprojectId,
        ownerAmountFiscalyear: '',
        contractorAmountFiscalyear: '',
        ownerAmountTotal: '',
        contractorAmountTotal: '',
      }
    );
  }

  function getOwnerAmount(bd: SubprojectBreakdownState): string {
    return bd[ownerField];
  }

  function getContractorAmount(bd: SubprojectBreakdownState): string {
    return bd[contractorField];
  }

  function updateBreakdown(
    subprojectId: string,
    patch: Partial<SubprojectBreakdownState>,
  ) {
    const existing = breakdowns.find((b) => b.subprojectId === subprojectId);
    if (existing) {
      onChange(
        breakdowns.map((b) =>
          b.subprojectId === subprojectId ? { ...b, ...patch } : b,
        ),
      );
    } else {
      onChange([
        ...breakdowns,
        {
          subprojectId,
          ownerAmountFiscalyear: '',
          contractorAmountFiscalyear: '',
          ownerAmountTotal: '',
          contractorAmountTotal: '',
          ...patch,
        },
      ]);
    }
  }

  function handleChange(
    subprojectId: string,
    field: AmountField,
    inputValue: string,
    total: number | null,
  ) {
    if (mode === '$') {
      updateBreakdown(subprojectId, { [field]: inputValue });
      return;
    }

    // % mode: keep what was typed, derive the dollars from it.
    const raw = sanitizePct(inputValue);
    setPctDrafts((prev) => ({ ...prev, [draftKey(subprojectId, field)]: raw }));
    updateBreakdown(subprojectId, { [field]: pctToAmount(raw, total) });
  }

  function getInputValue(
    subprojectId: string,
    field: AmountField,
    amount: string,
    total: number | null,
  ): string {
    if (mode === '$') return amount;
    const draft = pctDrafts[draftKey(subprojectId, field)];
    if (draft !== undefined) return draft;
    const n = parseAmount(amount);
    if (n === null || total === null || total === 0) return '';
    return ((n / total) * 100).toFixed(1);
  }

  function getComputedLabel(amount: string, total: number | null): string {
    if (mode === '$') {
      const n = parseAmount(amount);
      if (n === null || total === null || total === 0) return '';
      return `${((n / total) * 100).toFixed(1)}%`;
    } else {
      const n = parseAmount(amount);
      if (n === null) return '';
      return `$${formatDollar(n)}`;
    }
  }

  const ownerSum = breakdowns.reduce(
    (sum, b) => sum + (parseAmount(getOwnerAmount(b)) ?? 0),
    0,
  );
  const contractorSum = breakdowns.reduce(
    (sum, b) => sum + (parseAmount(getContractorAmount(b)) ?? 0),
    0,
  );
  const ownerRemaining =
    ownerTotalNum !== null ? ownerTotalNum - ownerSum : null;
  const contractorRemaining =
    contractorTotalNum !== null ? contractorTotalNum - contractorSum : null;

  return (
    <Box
      data-testid='subproject-breakdown'
      sx={{ borderLeft: '12px solid rgba(123, 188, 179, 0.5)' }}
    >
      {/* Sub-header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          py: 0.5,
          pl: 18,
          pr: 12,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'primary.main',
        }}
      >
        <Typography variant='caption' sx={{ color: 'text.secondary' }}>
          <Box component='span' sx={{ fontWeight: 700, fontSize: '0.8rem' }}>
            Subproject Breakdown
          </Box>
          <Box component='span' sx={{ fontStyle: 'italic' }}>
            {' — Total amount must be 100% allocated between all subprojects'}
          </Box>
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5, ml: 2, flexShrink: 0 }}>
          <Button
            size='small'
            variant={mode === '$' ? 'contained' : 'outlined'}
            onClick={() => changeMode('$')}
            sx={{ minWidth: 32, px: 0.75, py: 0.25, fontSize: '0.7rem' }}
          >
            $
          </Button>
          <Button
            size='small'
            variant={mode === '%' ? 'contained' : 'outlined'}
            onClick={() => changeMode('%')}
            sx={{ minWidth: 32, px: 0.75, py: 0.25, fontSize: '0.7rem' }}
          >
            %
          </Button>
        </Box>
      </Box>

      {/* Subproject rows */}
      {subprojects.map((sp, index) => {
        const bd = getBreakdown(sp.id);
        const ownerComputed = getComputedLabel(
          getOwnerAmount(bd),
          ownerTotalNum,
        );
        const contractorComputed = getComputedLabel(
          getContractorAmount(bd),
          contractorTotalNum,
        );

        return (
          <Box
            key={sp.id}
            sx={{
              display: 'grid',
              gridTemplateColumns: `${COL_LABEL} ${COL_AMOUNT} ${COL_AMOUNT} 40px`,
              alignItems: 'start',
              gap: 1,
              py: 0.5,
              pl: 18,
              pr: 1,
              borderBottom: '1px solid',
              borderColor: 'rgba(14, 70, 63, 0.2)',
              bgcolor: index % 2 === 0 ? '#D5E4D5' : '#CCDCCC',
              color: 'primary.main',
            }}
          >
            <Typography variant='body2' sx={{ alignSelf: 'center' }}>
              {sp.name}
            </Typography>
            <Box>
              <TextField
                size='small'
                value={getInputValue(
                  sp.id,
                  ownerField,
                  getOwnerAmount(bd),
                  ownerTotalNum,
                )}
                onChange={(e) =>
                  handleChange(sp.id, ownerField, e.target.value, ownerTotalNum)
                }
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position='start'>{mode}</InputAdornment>
                    ),
                  },
                }}
                sx={amountFieldSx}
              />
              {ownerComputed && (
                <Typography
                  variant='caption'
                  sx={{
                    color: 'rgba(14, 70, 63, 0.7)',
                    display: 'block',
                    textAlign: 'right',
                  }}
                >
                  {ownerComputed}
                </Typography>
              )}
            </Box>
            <Box>
              <TextField
                size='small'
                value={getInputValue(
                  sp.id,
                  contractorField,
                  getContractorAmount(bd),
                  contractorTotalNum,
                )}
                onChange={(e) =>
                  handleChange(
                    sp.id,
                    contractorField,
                    e.target.value,
                    contractorTotalNum,
                  )
                }
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position='start'>{mode}</InputAdornment>
                    ),
                  },
                }}
                sx={amountFieldSx}
              />
              {contractorComputed && (
                <Typography
                  variant='caption'
                  sx={{
                    color: 'rgba(14, 70, 63, 0.7)',
                    display: 'block',
                    textAlign: 'right',
                  }}
                >
                  {contractorComputed}
                </Typography>
              )}
            </Box>
            <Box />
          </Box>
        );
      })}

      {/* Remaining row */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: `${COL_LABEL} ${COL_AMOUNT} ${COL_AMOUNT} 40px`,
          alignItems: 'center',
          gap: 1,
          py: 0.5,
          pl: 18,
          pr: 1,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'primary.main',
        }}
      >
        <Typography
          variant='caption'
          sx={{
            color: 'text.secondary',
            fontStyle: 'italic',
            textAlign: 'right',
          }}
        >
          Remaining
        </Typography>
        <Typography
          variant='body2'
          data-testid='remaining-owner'
          sx={{
            textAlign: 'right',
            pr: 1,
            color:
              ownerRemaining !== null && ownerRemaining < 0
                ? 'error.main'
                : 'text.secondary',
          }}
        >
          {ownerRemaining !== null ? formatRemaining(ownerRemaining) : '—'}
        </Typography>
        <Typography
          variant='body2'
          data-testid='remaining-contractor'
          sx={{
            textAlign: 'right',
            pr: 1,
            color:
              contractorRemaining !== null && contractorRemaining < 0
                ? 'error.main'
                : 'text.secondary',
          }}
        >
          {contractorRemaining !== null
            ? formatRemaining(contractorRemaining)
            : '—'}
        </Typography>
        <Box />
      </Box>
    </Box>
  );
}
